import {
  HStack,
  Select,
  Text,
  Checkbox,
  CheckboxGroup,
  Stack,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useShipmentFacets } from "../hooks/useShipmentFacets";
import { useCptSlabLevels } from "../hooks/useCptSlabLevels";
import { useMemo, useEffect } from "react";
import { DispatchMode } from "../../../types";

export interface ShipmentFiltersProps {
  grouping: "customer" | "supplier";
  statusFilter: "undelivered" | "delivered" | "cancelled";
  isApiConfigReady: boolean;
  filters: {
    dispatchMode?: string | null;
    cptSlabLevels: number[];
  };
  onFiltersChange: (filters: {
    dispatchMode?: string | null;
    cptSlabLevels: number[];
  }) => void;
}

export default function ShipmentFilters({
  grouping,
  statusFilter,
  isApiConfigReady,
  filters,
  onFiltersChange,
}: ShipmentFiltersProps) {
  const { shipmentModes, cptSlabLevels, cptSlabLevelFacets, loading } =
    useShipmentFacets(statusFilter, isApiConfigReady);
  const { getLabel: getCptSlabLabel } = useCptSlabLevels();

  useEffect(() => {
    if (
      !loading &&
      shipmentModes.length > 0 &&
      filters.dispatchMode !== undefined
    ) {
      const availableModes = new Set(
        shipmentModes.map((item) => (item.mode === null ? null : item.mode))
      );
      const isInvalidMode = !availableModes.has(filters.dispatchMode);

      if (isInvalidMode) {
        const { dispatchMode, ...restFilters } = filters;
        onFiltersChange(restFilters);
      }
    }
  }, [loading, shipmentModes]);

  useEffect(() => {
    if (
      !loading &&
      cptSlabLevelFacets.length > 0 &&
      filters.cptSlabLevels.length > 0
    ) {
      const availableLevels = new Set(cptSlabLevelFacets.map((f) => f.level));
      const hasInvalidLevel = filters.cptSlabLevels.some(
        (level) => !availableLevels.has(level)
      );

      if (hasInvalidLevel) {
        onFiltersChange({
          ...filters,
          cptSlabLevels: [],
        });
      }
    }
  }, [loading, cptSlabLevelFacets]);

  const allCptSlabsSelected = useMemo(() => {
    if (cptSlabLevelFacets.length === 0) return false;
    if (filters.cptSlabLevels.length === 0) return true;
    return (
      filters.cptSlabLevels.length === cptSlabLevelFacets.length &&
      cptSlabLevelFacets.every((facet) =>
        filters.cptSlabLevels.includes(facet.level)
      )
    );
  }, [filters.cptSlabLevels, cptSlabLevelFacets]);

  const getCptSlabLevelsDisplayText = () => {
    if (filters.cptSlabLevels.length === 0 || allCptSlabsSelected) {
      return "All CPT Slabs";
    }
    if (filters.cptSlabLevels.length === 1) {
      return getCptSlabLabel(filters.cptSlabLevels[0]);
    }
    return `${filters.cptSlabLevels.length} Selected`;
  };

  return (
    <HStack spacing={4} align="center" flexWrap="nowrap">
      <HStack spacing={2} align="center" flexWrap="nowrap">
        <Text
          fontSize="sm"
          fontWeight="500"
          color="gray.800"
          whiteSpace="nowrap"
        >
          Dispatch Mode:
        </Text>
        <Select
          placeholder="All Modes"
          value={
            filters.dispatchMode === null
              ? "__NULL__"
              : filters.dispatchMode || ""
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === "" || value === "__ALL__") {
              const { dispatchMode, ...restFilters } = filters;
              onFiltersChange(restFilters);
            } else if (value === "__NULL__") {
              onFiltersChange({
                ...filters,
                dispatchMode: null,
              });
            } else {
              onFiltersChange({
                ...filters,
                dispatchMode: value,
              });
            }
          }}
          size="sm"
          bg="white"
          isDisabled={loading}
          minW="160px"
          borderRadius="md"
        >
          {shipmentModes.map((item) => {
            const modeValue = item.mode === null ? "__NULL__" : item.mode;
            const modeLabel =
              item.mode === null ? DispatchMode.UNKNOWN : item.mode;
            return (
              <option key={modeValue} value={modeValue}>
                {modeLabel}
              </option>
            );
          })}
        </Select>
      </HStack>

      <HStack spacing={2} align="center" flexWrap="nowrap">
        <Text
          fontSize="sm"
          fontWeight="500"
          color="gray.800"
          whiteSpace="nowrap"
        >
          CPT Slabs:
        </Text>
        <Popover placement="bottom-start" closeOnBlur={true}>
          <PopoverTrigger>
            <Button
              size="sm"
              rightIcon={<ChevronDownIcon />}
              minW="160px"
              bg="white"
              color="black"
              borderWidth="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
              isDisabled={loading}
              justifyContent="space-between"
            >
              <Text fontSize="sm"   color="black" fontWeight="normal" isTruncated>
                {loading ? "Loading..." : getCptSlabLevelsDisplayText()}
              </Text>
            </Button>
          </PopoverTrigger>
          <PopoverContent w="200px" maxH="300px" overflowY="auto">
            <PopoverBody p={2}>
              {loading ? (
                <Text fontSize="xs" color="gray.500" p={2}>
                  Loading...
                </Text>
              ) : cptSlabLevelFacets.length === 0 ? (
                <Text fontSize="xs" color="gray.500" p={2}>
                  No levels available
                </Text>
              ) : (
                <CheckboxGroup
                  value={
                    filters.cptSlabLevels.length === 0
                      ? cptSlabLevelFacets.map((f) => String(f.level))
                      : filters.cptSlabLevels.map(String)
                  }
                  onChange={(values) => {
                    const numericValues = values.map(Number);
                    const allSelected =
                      numericValues.length === cptSlabLevelFacets.length &&
                      cptSlabLevelFacets.every((facet) =>
                        numericValues.includes(facet.level)
                      );

                    onFiltersChange({
                      ...filters,
                      cptSlabLevels: allSelected ? [] : numericValues,
                    });
                  }}
                >
                  <Stack spacing={2}>
                    {cptSlabLevelFacets.map((facet) => {
                      return (
                        <Checkbox
                          key={facet.level}
                          value={String(facet.level)}
                          size="sm"
                          colorScheme="blue"
                        >
                          <Text fontSize="sm">
                            {getCptSlabLabel(facet.level)}
                          </Text>
                        </Checkbox>
                      );
                    })}
                  </Stack>
                </CheckboxGroup>
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </HStack>
    </HStack>
  );
}
